import { Card, MultiSelect } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import * as styles from './AuditSessionsFilters.css';
import { createFilterOptions } from './helpers';
import type { AuditSessionFilters, FilterOptions } from './types';

interface SessionsFilterGridProps {
  filters: AuditSessionFilters;
  onFiltersChange: (filters: AuditSessionFilters) => void;
  filterOptions: FilterOptions;
  isLoading?: boolean;
}

/**
 * Expandable filter grid component with all filter sections
 * Contains status, labels, topics, and languages filters
 */
export function SessionsFilterGrid({ filters, onFiltersChange, filterOptions, isLoading = false }: SessionsFilterGridProps) {
  // Generic filter change handler to reduce repetition
  const handleFilterChange = useCallback(
    (filterType: keyof AuditSessionFilters, selectedValues: string[]) => {
      const newFilters: AuditSessionFilters = { ...filters };

      if (filterType === 'status') {
        newFilters.status = selectedValues.filter(value => value !== 'all') as AuditSessionFilters['status'];
      } else {
        newFilters[filterType] = selectedValues;
      }

      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Individual filter handlers using the generic handler
  const handleStatusFilterChange = useCallback((selectedValues: string[]) => handleFilterChange('status', selectedValues), [handleFilterChange]);

  const handleLabelsFilterChange = useCallback((selectedValues: string[]) => handleFilterChange('labels', selectedValues), [handleFilterChange]);

  const handleTopicsFilterChange = useCallback((selectedValues: string[]) => handleFilterChange('topics', selectedValues), [handleFilterChange]);

  const handleLanguagesFilterChange = useCallback((selectedValues: string[]) => handleFilterChange('languages', selectedValues), [handleFilterChange]);

  // Prepare options for MultiSelect components using helper
  const { statusOptions, labelOptions, topicOptions, languageOptions } = useMemo(() => createFilterOptions(filterOptions), [filterOptions]);

  return (
    <Card>
      <div className={styles.filterCard}>
        <div className={styles.filterGrid}>
          {/* Status Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterLabel}>Status</div>
            <div className={styles.multiSelectWrapper}>
              <MultiSelect
                disabled={isLoading}
                options={statusOptions}
                placeholder="Select statuses..."
                selectedValues={filters.status.length === 0 ? ['all'] : filters.status}
                onSelectionChange={handleStatusFilterChange}
              />
            </div>
          </div>

          {/* Labels Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterLabel}>Labels</div>
            <div className={styles.multiSelectWrapper}>
              <MultiSelect
                disabled={isLoading}
                options={labelOptions}
                placeholder="Select labels..."
                selectedValues={filters.labels}
                onSelectionChange={handleLabelsFilterChange}
              />
            </div>
          </div>

          {/* Topics Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterLabel}>Topics</div>
            <div className={styles.multiSelectWrapper}>
              <MultiSelect
                disabled={isLoading}
                options={topicOptions}
                placeholder="Select topics..."
                selectedValues={filters.topics}
                onSelectionChange={handleTopicsFilterChange}
              />
            </div>
          </div>

          {/* Languages Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterLabel}>Languages</div>
            <div className={styles.multiSelectWrapper}>
              <MultiSelect
                disabled={isLoading}
                options={languageOptions}
                placeholder="Select languages..."
                selectedValues={filters.languages}
                onSelectionChange={handleLanguagesFilterChange}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
